import { getAssetStatuses } from "api/assets.api";
import { getDeviceInfo } from "api/devices.api";
import { getMapAssets, getMapDevices, getMapUsers } from "api/map.api";
import { getVenueFile } from "api/venue.api";
import { differenceInMinutes } from "date-fns";
import { FieldState, FormState } from "formstate";
import L from "leaflet";
import { min } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { Asset, AssetStatus } from "types/asset";
import { Device } from "types/device";
import { User } from "types/userInfo";
import { BuildingGeoJsonObject, LevelGeoJsonObject, UnitGeoJsonObject, Venue } from "types/venue";
import { LabelValuePair } from "utils/common";
import { AssetPermissions } from "views/config/roles/RoleConfigDto";

export const DISPLAY_ASSET_NAME_LIMIT = 30
// 1 means clustring is disabled. The value of enable clustring is 22
export const MAX_ZOOM_FOR_DISPLAY_CLUSTERS = 1

const initialVenueObj: Venue = {
  venue_id: '',
  name: '',
  latitude: 0,
  longitude: 0,
  latest_versions: [{ name: '', dataset: '', released_at: '' }],
};

type FilterForm = FormState<{
  searchTerm: FieldState<string>,
  searchIn: FieldState<string>,
  show: FieldState<string[]>,
  assetTypes: FieldState<string[]>,
  owners: FieldState<string[]>,
  assetStatuses: FieldState<string[]>,
  displayAssetName: FieldState<boolean>,
  displayUserName: FieldState<boolean>,
  displayDeviceId: FieldState<boolean>,
}>

export class MapStore {
  root: RootStore;
  loading = true;
  loadingAssets = false
  loadingUsers = false
  loadingDevices = false
  assetStatuses: AssetStatus[] = []
  assetTypes: LabelValuePair[] = []
  owners: LabelValuePair[] = []
  buildings!: BuildingGeoJsonObject
  levels!: LevelGeoJsonObject
  units!: UnitGeoJsonObject

  users: User[] = []
  assets: Asset[] = []
  devices: Device[] = []

  selectedBuildingId: string | null = null
  selectedFloor: number | null = null
  selectedBeaconId: string | null = null
  selectedDevice: Device | null = null
  selectedDeviceFull: Device | null = null

  GLOBAL_selectedMapVenueObj: Venue | null = null
  GLOBAL_buildingSelectedID: string | null = null
  GLOBAL_mapFloorOrdinal: number | null = null

  selectedVenueObj: Venue = initialVenueObj

  mapZoom: number = 14
  mapMoveVersion: number = 0 // to conrtol re-renders on map moving
  visibleAssetsCount: number = 0
  visibleUsersCount: number = 0
  visibleDevicesCount: number = 0

  abortController = new AbortController();

  createFilterForm() {

    this.assetTypes = this.root.userRolePermissionStore.getAssetTypes(AssetPermissions.view)
    this.owners = this.root.userRolePermissionStore.getAssetOwners(AssetPermissions.view)

    const show = this.root.userRolePermissionStore.canViewDevices
      ? ["assets", "devices"]
      : ["assets", "users"]


    return new FormState({
      searchTerm: new FieldState(""),
      searchIn: new FieldState(''),
      show: new FieldState(show),
      assetTypes: new FieldState<string[]>(this.assetTypes.map(r => r.value)),
      owners: new FieldState<string[]>(this.owners.map(r => r.value)),
      assetStatuses: new FieldState<string[]>([...this.assetStatuses.map(r => r.asset_status_id), "__null"]),
      displayAssetName: new FieldState(false),
      displayUserName: new FieldState(false),
      displayDeviceId: new FieldState(false)
    })
  }

  filterForm: FilterForm

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      loading: observable,
      loadingAssets: observable,
      loadingUsers: observable,
      loadingDevices: observable,
      buildings: observable,
      levels: observable,
      units: observable,
      selectedBuildingId: observable,
      selectedFloor: observable,
      selectedBeaconId: observable,
      filterForm: observable,
      users: observable,
      assets: observable,
      devices: observable,
      abortController: observable,
      mapZoom: observable,
      mapMoveVersion: observable,
      visibleAssetsCount: observable,
      visibleUsersCount: observable,
      visibleDevicesCount: observable,
      selectedDevice: observable,
      selectedDeviceFull: observable,

      reset: action.bound,
      loadData: action.bound,
      selectBuilding: action.bound,
      selectFloor: action.bound,
      selectBeacon: action.bound,
      getBuildingLevels: action.bound,
      getBuildingAssets: action.bound,
      getBuildingUsers: action.bound,
      getBuildingDevices: action.bound,
      getBuidlingAssetsCount: action.bound,
      getBuidlingUsersCount: action.bound,
      getBuidlingDevicesCount: action.bound,
      getOrdinalLevels: action.bound,
      getOrdinalUnits: action.bound,
      loadAssets: action.bound,
      loadUsers: action.bound,
      loadDevices: action.bound,
      getFirstNotEmptyFloor: action.bound,
      getAssetSearchParams: action.bound,
      resetFilterForm: action.bound,
      setMapZoom: action.bound,
      handleMapMove: action.bound,
      setSelectedDevice: action.bound,

      selectedBuildingOrdinals: computed,
      selectedBuildingLevels: computed,
      outdoorAssets: computed,
      outdoorUsers: computed,
      outdoorDevices: computed,
      showAssetMarkers: computed,
      showUserMarkers: computed,
    });

    this.filterForm = this.createFilterForm()
  }

  reset() {
    this.loading = true;
    this.loadingAssets = false;
    this.loadingUsers = false;
    this.loadingDevices = false;
    this.users = []
    this.assets = []
    this.devices = []

    this.selectedBuildingId = null
    this.selectedFloor = null
    this.selectedBeaconId = null
    this.selectedDevice = null
    this.selectedDeviceFull = null

    this.selectedVenueObj = initialVenueObj
    this.abortController = new AbortController();
    this.filterForm = this.createFilterForm()

  }

  setSelectedDevice(device: Device | null) {
    if (device === null) {
      this.selectedDevice = null
      this.selectedDeviceFull = null
      return
    }
    this.selectedBeaconId = null;
    this.selectedDevice = device

    return getDeviceInfo(device?.device_id)
      .then((res) => {
        this.selectedDeviceFull = res
      })
  }

  resetFilterForm() {
    this.filterForm = this.createFilterForm()
  }

  selectBuilding(building_id: string | null) {
    this.selectedBuildingId = building_id
    const ordinals = this.getBuildingOrdinals(building_id)
    this.selectFloor(ordinals.length > 0 ? ordinals[0] : null)
  }
  selectFloor(floor: number | null) {
    this.selectedFloor = floor
    this.selectBeacon(null)
  }
  selectBeacon(id: string | null) {
    this.selectedBeaconId = id
    this.setSelectedDevice(null)
  }
  setMapZoom(zoom: number) {
    this.mapZoom = zoom
  }
  handleMapMove(bounds: L.LatLngBounds) {
    const assetsCount = this.assets.filter(asset => {
      const lat = asset.location?.coordinates.latitude;
      const lng = asset.location?.coordinates.longitude;
      return lat && lng && bounds.contains(L.latLng(lat, lng));
    }).length;
    this.visibleAssetsCount = assetsCount
    if (assetsCount > DISPLAY_ASSET_NAME_LIMIT) {
      this.filterForm.$.displayAssetName.onChange(false)
    }

    const usersCount = this.users.filter(user => {
      const lat = user.location?.coordinates.latitude;
      const lng = user.location?.coordinates.longitude;
      return lat && lng && bounds.contains(L.latLng(lat, lng));
    }).length;
    this.visibleUsersCount = usersCount
    if (usersCount > DISPLAY_ASSET_NAME_LIMIT) {
      this.filterForm.$.displayUserName.onChange(false)
    }

    const devicesCount = this.devices.filter(device => {
      const lat = device.location?.coordinates.latitude;
      const lng = device.location?.coordinates.longitude;
      return lat && lng && bounds.contains(L.latLng(lat, lng));
    }).length;
    this.visibleDevicesCount = devicesCount
    if (devicesCount > DISPLAY_ASSET_NAME_LIMIT) {
      this.filterForm.$.displayDeviceId.onChange(false)
    }

    this.mapMoveVersion = this.mapMoveVersion + 1
  }

  getActiveUsers(users: User[]) {

    const dateNow = new Date();
    return users.filter((user) => {
      const formattedEstimateDate = new Date(user.last_update_timestamp);
      const diffInMins = differenceInMinutes(dateNow, formattedEstimateDate);
      // returns true if last update time is before time last hour from now
      return diffInMins >= 0 && diffInMins < 60;
    });
  }

  getAssetSearchParams() {
    const params: Record<string, any> = {}
    if (this.filterForm.$.searchTerm.value !== "" && this.filterForm.$.searchIn.value === "assets")
      params["name_filter"] = this.filterForm.$.searchTerm.value

    if (this.filterForm.$.assetTypes.value.length < this.assetTypes.length)
      params["asset_type_ids_filter"] = this.filterForm.$.assetTypes.value
    if (this.filterForm.$.assetTypes.value.length === 0)
      params["asset_type_ids_filter"] = null

    if (this.filterForm.$.owners.value.length < this.owners.length)
      params["owner_ids_filter"] = this.filterForm.$.owners.value
    if (this.filterForm.$.owners.value.length === 0)
      params["owner_ids_filter"] = null

    if (this.filterForm.$.assetStatuses.value.length < this.assetStatuses.length + 1)
      params["status_ids_filter"] = this.filterForm.$.assetStatuses.value
    if (this.filterForm.$.assetStatuses.value.length === 0)
      params["status_ids_filter"] = null

    return params
  }

  updateSelectedBeacon(assets: Asset[]) {
    //if selected beacon is not in assets list, deselect it
    if (this.selectedBeaconId) {
      if (!assets.find(asset => asset.assigned_beacon?.id === this.selectedBeaconId))
        this.selectBeacon(null)
    }
  }

  async loadAssets() {
    this.loadingAssets = true
    const params = this.getAssetSearchParams()

    const isFirstLoad = this.assets.length === 0
    const loadedAssets = []
    while (true) {
      const res = await getMapAssets(this.root.venue.venue_id, params)
      params.next = res.next
      loadedAssets.push(...res.items)
      //loading assets for first time, so that show loaded assets while loading the rest
      if (isFirstLoad) {
        this.assets.push(...res.items)
      }
      if (loadedAssets.length === res.total)
        break
    }
    if (!isFirstLoad)
      this.assets = loadedAssets

    this.updateSelectedBeacon(this.assets)
    this.loadingAssets = false
    return this.assets
  }

  async loadUsers() {
    this.loadingUsers = true
    const params: Record<string, any> = {}
    if (this.filterForm.$.searchTerm.value !== "" && this.filterForm.$.searchIn.value === "users")
      params["name_filter"] = this.filterForm.$.searchTerm.value

    const isFirstLoad = this.users.length === 0
    let loadedUsers = []
    while (true) {
      const res = await getMapUsers(this.root.venue.venue_id, params)
      params.next = res.next
      loadedUsers.push(...res.items)
      // loading users for first time, so that show loaded users while loading the rest
      if (isFirstLoad) {
        this.users.push(...this.getActiveUsers(res.items))
      }
      if (loadedUsers.length === res.total)
        break
    }
    if (!isFirstLoad)
      this.users = this.getActiveUsers(loadedUsers)

    this.loadingUsers = false
    return this.users
  }

  async loadDevices() {
    this.loadingDevices = true
    const params: Record<string, any> = {}
    if (this.filterForm.$.searchTerm.value !== "" && this.filterForm.$.searchIn.value === "devices")
      params["device_id_filter"] = this.filterForm.$.searchTerm.value

    const isFirstLoad = this.devices.length === 0
    let loadedDevices = []
    while (true) {
      const res = await getMapDevices(this.root.venue.venue_id, params)
      params.next = res.next
      loadedDevices.push(...res.items)
      // loading devices for first time, so that show loaded devices while loading the rest
      if (isFirstLoad) {
        this.devices.push(...res.items)
      }
      if (loadedDevices.length === res.total)
        break
    }
    if (!isFirstLoad)
      this.devices = loadedDevices

    this.loadingDevices = false
    return this.devices
  }

  loadData() {
    this.loading = true
    const venue_id = this.root.venue.venue_id
    const venueUrl = this.root.webConfig.services.venue_service

    return Promise.all([
      this.root.userRolePermissionStore.canViewAssets ? getAssetStatuses() : [],
      getVenueFile(venueUrl, venue_id, 'building.geojson'),
      getVenueFile(venueUrl, venue_id, 'level.geojson'),
      getVenueFile(venueUrl, venue_id, 'unit.geojson'),
    ]).then(async (res) => {
      this.assetStatuses = res[0]
      this.buildings = res[1]
      this.levels = res[2]
      this.units = res[3]

      this.filterForm = this.createFilterForm()
      this.loading = false
    })
  }

  getBuildingLevels(building_id: string | null) {
    if (!building_id)
      return []
    return this.levels.features.filter(feature =>
      feature.properties.building_ids?.includes(building_id)
    )
  }

  getBuildingOrdinals(building_id: string | null): number[] {
    if (!building_id)
      return []
    return this.getBuildingLevels(building_id)
      .filter((value: any, index: number, self: any) => {
        return self.findIndex((v: any) => v.properties.ordinal === value.properties.ordinal) === index;
      })
      .sort((a, b) => a.properties.ordinal > b.properties.ordinal ? 1 : -1)
      .map(r => r.properties.ordinal)
  }

  get selectedBuildingLevels() {
    return this.getBuildingLevels(this.selectedBuildingId)
  }

  get selectedBuildingOrdinals() {
    return this.getBuildingOrdinals(this.selectedBuildingId)
  }

  getOrdinalLevels(building_id: string, ordinal: number) {
    return this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
  }

  getOrdinalUnits(building_id: string, ordinal: number) {
    const levels = this.getOrdinalLevels(building_id, ordinal).map(r => r.id)
    return this.units.features.filter(feature => levels.includes(feature.properties.level_id))
  }

  getBuildingAssets(building_id: string | null): Record<number, Asset[]> {
    if (!building_id)
      return {}
    const list: Record<number, Asset[]> = {}
    this.getBuildingOrdinals(building_id).forEach(ordinal => {
      const levels = this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
      list[ordinal] = this.assets
        .filter(asset =>
          asset.assigned_beacon !== null &&
          asset.location.building_level !== null &&
          asset.location.building_level !== undefined &&
          asset.location.building_level.possible_buildings.length > 0 &&
          !asset.location.is_outdoors)
        .filter(asset => levels.map(r => r.id).includes(asset.location.building_level.level.id))
    })
    return list
  }

  getBuidlingAssetsCount(building_id: string | null) {
    const list = this.getBuildingAssets(building_id)
    return Object.values(list).reduce((sum, v) => sum + v.length, 0)
  }

  getBuildingUsers(building_id: string | null): Record<number, User[]> {
    if (!building_id)
      return {}
    const list: Record<number, User[]> = {}
    this.getBuildingOrdinals(building_id).forEach(ordinal => {
      const levels = this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
      list[ordinal] = this.users
        .filter(user =>
          user.location &&
          user.location.coordinates &&
          user.location.building_level &&
          !user.location.is_outdoors)
        .filter(user => levels.map(r => r.id).includes(user.location.building_level.level.id))
    })
    return list
  }

  getBuidlingUsersCount(building_id: string | null) {
    const list = this.getBuildingUsers(building_id)
    return Object.values(list).reduce((sum, v) => sum + v.length, 0)
  }

  getBuildingDevices(building_id: string | null): Record<number, Device[]> {
    if (!building_id)
      return {}
    const list: Record<number, Device[]> = {}
    this.getBuildingOrdinals(building_id).forEach(ordinal => {
      const levels = this.getBuildingLevels(building_id).filter(f => f.properties.ordinal === ordinal)
      list[ordinal] = this.devices
        .filter(device =>
          device.location.building_level !== null &&
          device.location.building_level !== undefined &&
          device.location.building_level.possible_buildings.length > 0 &&
          !device.location.is_outdoors)
        .filter(device => levels.map(r => r.id).includes(device.location.building_level.level.id))
    })
    return list
  }

  getBuidlingDevicesCount(building_id: string | null) {
    const list = this.getBuildingDevices(building_id)
    return Object.values(list).reduce((sum, v) => sum + v.length, 0)
  }

  getFirstNotEmptyFloor(building_id: string): number {

    let ordinals: Record<number, any[]> = {}

    if (this.showAssetMarkers && this.assets.length > 0)
      ordinals = this.getBuildingAssets(building_id)
    if (this.showUserMarkers && this.users.length > 0)
      ordinals = this.getBuildingUsers(building_id)
    if (this.showDeviceMarkers && this.devices.length > 0)
      ordinals = this.getBuildingDevices(building_id)

    const ordinalsList = Object.keys(ordinals).sort((a, b) => parseInt(a) > parseInt(b) ? 1 : -1)
    for (const ordinal of ordinalsList)
      if (ordinals[parseInt(ordinal)].length > 0)
        return parseInt(ordinal)

    return min(Object.keys(ordinals).map(r => parseInt(r))) || 0

  }

  get outdoorAssets() {
    return this.assets.filter(asset => asset.assigned_beacon && asset.location.is_outdoors)
  }

  get outdoorUsers() {
    return this.users.filter(user => user.location.is_outdoors)
  }

  get outdoorDevices() {
    return this.devices.filter(device => device.location.is_outdoors)
  }

  get showAssetMarkers() {
    return this.filterForm.$.show.value.includes("assets")
      && (this.filterForm.$.searchIn.value === "assets" || this.filterForm.$.searchIn.value === "")
  }

  get showUserMarkers() {
    return this.filterForm.$.show.value.includes("users")
      && (this.filterForm.$.searchTerm.value !== ""
        ? (this.filterForm.$.searchIn.value === "users" || this.filterForm.$.searchIn.value === "")
        : true)
  }

  get showDeviceMarkers() {
    return this.filterForm.$.show.value.includes("devices")
      && (this.filterForm.$.searchTerm.value !== ""
        ? (this.filterForm.$.searchIn.value === "devices" || this.filterForm.$.searchIn.value === "")
        : true)
  }


}
